import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card, EmptyState, ErrorNotice, PageHeader, Skeleton, Table, TabPanel, Tabs } from '@/components/ui';
import { useI18n, formatRelative } from '@/i18n';
import { api } from '@/lib/api';
import { money } from '@/lib/format';
import type { EV } from '@/lib/types';
import { EVALUATOR_ACTIVE, EvaluatorEvChip, useEvNextStep } from './common';

type Tab = 'inProgress' | 'done' | 'cancelled';

export function EvaluatorRequests() {
  const { t, locale, localTitle } = useI18n();
  const nextStep = useEvNextStep();
  const [tab, setTab] = useState<Tab>('inProgress');
  const status = tab === 'inProgress' ? EVALUATOR_ACTIVE.join(',') : tab === 'done' ? 'completed' : 'cancelled';
  const list = useQuery({ queryKey: ['evaluator', 'requests', tab], queryFn: () => api.get<{ items: EV[] }>(`/api/evaluator/requests?status=${status}&limit=100`) });
  const rows = list.data?.items ?? [];
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/evaluator', label: t('nav.dashboard') }, { label: t('nav.myEvaluations') }]} title={t('nav.myEvaluations')} />
      <Tabs<Tab> id="evaluator-requests" value={tab} onChange={setTab} items={[{ key: 'inProgress', label: t('evaluator.requests.tabs.inProgress') }, { key: 'done', label: t('evaluator.requests.tabs.done') }, { key: 'cancelled', label: t('evaluator.requests.tabs.cancelled') }]} />
      <TabPanel id="evaluator-requests" value={tab}>
        {list.isLoading ? <Skeleton lines={6} /> : list.error ? <ErrorNotice error={list.error} retry={() => void list.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} /> : rows.length ? (
          <Card className="p-0 overflow-hidden">
            <Table className="border-0 rounded-none">
              <thead><tr><th>{t('app.number')}</th><th>{t('app.property')}</th><th>{t('app.owner')}</th><th>{t('evaluator.requests.yourFee')}</th><th>{t('app.status')}</th><th>{t('app.nextStep')}</th><th>{t('app.updated')}</th></tr></thead>
              <tbody>
                {rows.map((e) => {
                  const ns = nextStep(e);
                  return (
                    <tr key={e.id}>
                      <td className="num"><Link to={`/evaluator/requests/${e.id}`}>{e.number}</Link></td>
                      <td>{localTitle(e)}</td>
                      <td>{e.ownerName ?? '—'}</td>
                      <td className="num">{money(e.feeHalalas, locale)}</td>
                      <td><EvaluatorEvChip ev={e} /></td>
                      <td className={ns.muted ? 'text-muted' : ''}>{ns.muted ? ns.text : <Link to={`/evaluator/requests/${e.id}`}>{ns.text}</Link>}</td>
                      <td className="text-muted text-[13px]">{formatRelative(e.updatedAt, locale)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card>
        ) : <EmptyState title={t('broker.requests.empty')} />}
      </TabPanel>
    </div>
  );
}
